import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import {useSession} from "./auth";
import {
  createApiKey,
  createCreditCheckout,
  deleteProviderCredential,
  fetchDashboard,
  fetchPublicUsage,
  revokeApiKey,
  setProviderCredential,
  setProviderMode,
  type BillingMode,
  type CreatedApiKey,
  type DashboardData,
  type ProviderMode,
  type ProviderSetting,
  type PublicUsageBucket,
} from "./api";

type RouteName = "overview" | "models" | "providers" | "keys" | "usage" | "billing";

const navigation: Array<{route: RouteName; label: string; path: string}> = [
  {route: "overview", label: "Overview", path: "/dashboard"},
  {route: "models", label: "Models", path: "/dashboard/models"},
  // The route keeps its /providers path: the label is what customers call the
  // feature, and changing the URL would break every link already handed out.
  {route: "providers", label: "BYOK", path: "/dashboard/providers"},
  {route: "keys", label: "API keys", path: "/dashboard/api-keys"},
  {route: "usage", label: "Usage", path: "/dashboard/usage"},
  {route: "billing", label: "Billing", path: "/dashboard/billing"},
];

function routeFromPath(path: string): RouteName {
  if (path.includes("/models")) return "models";
  if (path.includes("/providers")) return "providers";
  if (path.includes("/api-keys")) return "keys";
  if (path.includes("/usage")) return "usage";
  if (path.includes("/billing")) return "billing";
  return "overview";
}

function navigate(path: string) {
  history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const update = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);
  return pathname;
}

function formatDollars(micros: number) {
  return new Intl.NumberFormat("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2})
    .format(micros / 1_000_000);
}

function formatNumber(value: number) {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatDate(value: string | null) {
  if (!value) return "Never";
  return new Intl.DateTimeFormat("en", {month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"})
    .format(new Date(value));
}

function formatDay(value: string) {
  return new Intl.DateTimeFormat("en", {month: "short", day: "numeric", year: "numeric"}).format(new Date(value));
}

function recentBuckets(buckets: DashboardData["usageBuckets"]) {
  const cutoff = Date.now() - 168 * 3_600_000;
  return buckets.filter((bucket) => Date.parse(bucket.hour) >= cutoff);
}

type IconName =
  | RouteName
  | "chevron" | "copy" | "plus" | "logout" | "refresh"
  | "key" | "trash" | "alert" | "info" | "lock" | "back" | "forward" | "close" | "check";

function Icon({name}: {name: IconName}) {
  const paths: Record<string, string> = {
    overview: "M4 13h6V4H4v9Zm10 7h6v-9h-6v9ZM4 20h6v-3H4v3Zm10-13h6V4h-6v3Z",
    models: "m12 3 8 4.5v9L12 21l-8-4.5v-9L12 3Zm0 0v9m8-4.5-8 4.5m-8-4.5 8 4.5",
    providers: "M7 3v6a5 5 0 0 0 10 0V3M9 3v4m6-4v4M12 14v7",
    keys: "M15.5 7.5a4.5 4.5 0 1 0-3.9 4.46L14 14.36V17h2.64l1.5 1.5H21V16l-5.54-5.54A4.48 4.48 0 0 0 15.5 7.5Z",
    usage: "M4 19V9m5 10V5m6 14v-7m5 7V3",
    billing: "M3 6h18v12H3V6Zm0 4h18M7 15h3",
    chevron: "m8 10 4 4 4-4",
    copy: "M9 9h10v10H9V9Zm-4 6H3V3h12v2",
    plus: "M12 5v14m-7-7h14",
    logout: "M10 5H5v14h5m4-4 4-3-4-3m4 3H9",
    refresh: "M20 6v5h-5M4 18v-5h5m9.2-5A7 7 0 0 0 6.4 6M5.8 17A7 7 0 0 0 17.6 18",
    key: "M21 3 11 13M15.5 8.5 19 12M8 12a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z",
    trash: "M4 7h16M9 7V4.6A.6.6 0 0 1 9.6 4h4.8a.6.6 0 0 1 .6.6V7M6.5 7l.8 12.4a1.6 1.6 0 0 0 1.6 1.5h6.2a1.6 1.6 0 0 0 1.6-1.5L17.5 7M10 11v6m4-6v6",
    alert: "M12 9.5v4M12 17v.8M10.3 4.4 2.6 17.6A1.9 1.9 0 0 0 4.3 20.5h15.4a1.9 1.9 0 0 0 1.7-2.9L13.7 4.4a1.9 1.9 0 0 0-3.4 0Z",
    info: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0ZM12 16.5v-5M12 8v.8",
    lock: "M7 10.5v-3a5 5 0 0 1 10 0v3M5.8 10.5h12.4a.8.8 0 0 1 .8.8v8.4a.8.8 0 0 1-.8.8H5.8a.8.8 0 0 1-.8-.8v-8.4a.8.8 0 0 1 .8-.8ZM12 15v2.2",
    back: "m14.5 5-7 7 7 7",
    forward: "m9.5 5 7 7-7 7",
    close: "m6 6 12 12M18 6 6 18",
    check: "m5 12.5 4.6 4.5L19 6.8",
  };
  return <svg aria-hidden="true" viewBox="0 0 24 24"><path d={paths[name]} /></svg>;
}

function AccountMenu({email, name, signOut}: {
  email: string;
  name: string;
  signOut: (options?: {returnTo?: string}) => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnPointerDown = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnPointerDown);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnPointerDown);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <div className="account-menu" ref={menuRef}>
      <button className="account-trigger" type="button" aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((current) => !current)}>
        <span className="avatar">{name.slice(0, 1).toUpperCase()}</span>
        <span>{name}</span>
        <Icon name="chevron" />
      </button>
      {open && (
        <div className="account-popover" role="menu">
          <div className="account-identity"><strong>{name}</strong><span>{email}</span></div>
          <button type="button" role="menuitem" onClick={() => signOut({returnTo: window.location.origin})}>
            <Icon name="logout" />Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function LandingPage({user, signIn, signUp}: {
  user: unknown;
  signIn: (options?: {state?: unknown}) => Promise<void>;
  signUp: (options?: {state?: unknown}) => Promise<void>;
}) {
  return (
    <div className="app-shell">
      <header className="site-header">
        <a className="wordmark" href="/" aria-label="Hashset home"><img src="/brand/wordmark-black.svg" alt="Hashset" /></a>
        <a className="public-link" href="/usage">Usage</a>
        <div className="landing-auth">
          {user ? (
            <button className="text-button" onClick={() => navigate("/dashboard")}>Dashboard</button>
          ) : (
            <button className="text-button" onClick={() => void signIn({state: {returnTo: "/dashboard"}})}>Sign in</button>
          )}
        </div>
      </header>
      <main className="landing-main">
        <img className="hero-grid" src="/brand/grid-circle.svg?v=1920" alt="" aria-hidden="true" />
        <section className="hero">
          <h1>It runs your LLMs.<br />Don’t worry about it.</h1>
          <button className="primary" onClick={() => user ? navigate("/dashboard") : void signUp({state: {returnTo: "/dashboard"}})}>
            {user ? "Open Dashboard" : "Sign Up"}
          </button>
        </section>
      </main>
    </div>
  );
}

function PublicUsagePage({user, signIn}: {
  user: unknown;
  signIn: (options?: {state?: unknown}) => Promise<void>;
}) {
  const [usage, setUsage] = useState<PublicUsageBucket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void fetchPublicUsage()
      .then((rows) => { if (active) setUsage(rows); })
      .catch((cause) => { if (active) setError(cause instanceof Error ? cause.message : "Usage analytics are unavailable"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const chartBuckets = usage.map((bucket) => ({
    ...bucket,
    model: "All traffic",
    spendMicros: 0,
  }));
  const totals = usage.reduce((summary, bucket) => ({
    requests: summary.requests + bucket.requests,
    inputTokens: summary.inputTokens + bucket.inputTokens,
    outputTokens: summary.outputTokens + bucket.outputTokens,
  }), {requests: 0, inputTokens: 0, outputTokens: 0});

  return (
    <div className="public-usage-shell">
      <header className="site-header public-usage-header">
        <a className="wordmark" href="/" aria-label="Hashset home"><img src="/brand/wordmark-black.svg" alt="Hashset" /></a>
        <div className="public-usage-nav"><a href="/">Home</a>{user ? <a href="/dashboard">Dashboard</a> : <button className="text-button" onClick={() => void signIn({state: {returnTo: "/usage"}})}>Sign in</button>}</div>
      </header>
      <main className="public-usage-main">
        <header className="public-usage-title"><span>Live network activity</span><h1>Global usage</h1><p>Aggregate inference activity across Hashset. Updated as requests complete.</p></header>
        <div className="public-stat-grid" aria-label="Last 28 days">
          <div><span>Total tokens</span><strong>{compactNumber(totals.inputTokens + totals.outputTokens)}</strong></div>
          <div><span>Requests</span><strong>{compactNumber(totals.requests)}</strong></div>
          <div><span>Input tokens</span><strong>{compactNumber(totals.inputTokens)}</strong></div>
          <div><span>Output tokens</span><strong>{compactNumber(totals.outputTokens)}</strong></div>
        </div>
        <section className="panel usage-chart-panel public-chart-panel">
          <div className="panel-heading"><div><h2>Token consumption</h2><p>Daily aggregate tokens over the last four weeks.</p></div><span>Public aggregate</span></div>
          {error ? <EmptyState title="Usage unavailable" description={error} /> : loading ? <LoadingState /> : <UsageChart buckets={chartBuckets} ariaLabel="Daily global token consumption over the last 28 days" />}
        </section>
      </main>
    </div>
  );
}

function Dashboard({signOut, email, name}: {
  signOut: (options?: {returnTo?: string}) => void;
  email: string;
  name: string;
}) {
  const pathname = usePathname();
  const route = routeFromPath(pathname);
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newKey, setNewKey] = useState<CreatedApiKey | null>(null);
  const [providerNotice, setProviderNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setData(await fetchDashboard());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Hashset could not be reached. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  // BYOK access gates its own nav tab and page. The tab is filtered out below
  // rather than shown then hidden, and a direct hit on /dashboard/providers is
  // sent back to the overview once the loaded data confirms there is no access.
  const byokEnabled = data?.byokEnabled ?? false;
  useEffect(() => {
    if (!loading && data && route === "providers" && !data.byokEnabled) navigate("/dashboard");
  }, [loading, data, route]);
  const visibleNavigation = navigation.filter((item) => item.route !== "providers" || byokEnabled);

  async function handleCreateKey(nameValue: string) {
    setBusy(true);
    setError(null);
    try {
      setNewKey(await createApiKey(nameValue));
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "API key creation failed");
    } finally { setBusy(false); }
  }

  async function handleRevokeKey(id: string) {
    setBusy(true);
    try {
      await revokeApiKey(id);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "API key revocation failed");
    } finally { setBusy(false); }
  }

  async function runProviderChange(change: () => Promise<unknown>, failure: string): Promise<ProviderChangeOutcome> {
    setBusy(true);
    setError(null);
    setProviderNotice(null);
    const outcome = await providerChangeOutcome(change, failure);
    if (outcome.ok) {
      if (outcome.warning) setProviderNotice(outcome.warning);
      await refresh();
    }
    setBusy(false);
    return outcome;
  }

  // The page notice is the wrong place for a failure raised inside the add
  // dialog: it renders behind the modal. That path reports the outcome to the
  // dialog instead, which shows it next to the key the customer just typed.
  async function applyProviderChange(change: () => Promise<unknown>, failure: string): Promise<boolean> {
    const outcome = await runProviderChange(change, failure);
    if (!outcome.ok) setError(outcome.error);
    return outcome.ok;
  }

  async function handleCheckout(amount: number) {
    setBusy(true);
    setError(null);
    try {
      window.location.assign(await createCreditCheckout(amount));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Checkout could not be started");
      setBusy(false);
    }
  }

  return (
    <div className="dashboard-shell">
      <header className="dashboard-topbar">
        <div className="dashboard-frame">
          <div className="dashboard-navbar">
            <a className="dashboard-wordmark" href="/" aria-label="Hashset home"><img src="/brand/wordmark-black.svg" alt="Hashset" /></a>
            <AccountMenu email={email} name={name} signOut={signOut} />
          </div>
          <nav className="dashboard-navigation" aria-label="Dashboard">
            {visibleNavigation.map((item) => (
              <a key={item.route} href={item.path} className={route === item.route ? "active" : ""}
                aria-current={route === item.route ? "page" : undefined}
                onClick={(event) => { event.preventDefault(); navigate(item.path); }}>
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="dashboard-content">
          <header className="dashboard-header">
            <div><h1>{navigation.find((item) => item.route === route)?.label}</h1></div>
            <button className="icon-button" aria-label="Refresh dashboard" onClick={() => void refresh()} disabled={loading}><Icon name="refresh" /></button>
          </header>

          {error && <div className="notice error-notice"><strong>Something went wrong</strong><span>{error}</span></div>}
          {providerNotice && <div className="notice success-notice"><strong>Key saved</strong><span>{providerNotice}</span></div>}
          {new URLSearchParams(window.location.search).get("checkout") === "success" && (
            <div className="notice success-notice"><strong>Payment received</strong><span>Your wallet is being refreshed from Autumn.</span></div>
          )}

          {loading ? <LoadingState /> : data ? (
            <>
              {route === "overview" && <Overview data={data} />}
              {route === "models" && (
                <ModelsPage models={data.models} performance={data.modelPerformance}
                  settings={data.providerSettings} byokEnabled={byokEnabled} />
              )}
              {route === "providers" && byokEnabled && (
                <ProvidersPage
                  settings={data.providerSettings}
                  busy={busy}
                  onMode={(provider, mode) => applyProviderChange(
                    () => setProviderMode(provider, mode),
                    "The provider mode could not be changed",
                  )}
                  onSaveKey={(provider, key, attested) => runProviderChange(
                    () => setProviderCredential(provider, key, attested),
                    "The provider key could not be saved",
                  )}
                  onAddKey={(provider, key, attested) => runProviderChange(
                    () => addProviderKeyChange(
                      () => setProviderCredential(provider, key, attested),
                      () => setProviderMode(provider, "byok"),
                    ),
                    "The provider key could not be saved",
                  )}
                  onDeleteKey={(provider) => applyProviderChange(
                    () => deleteProviderCredential(provider),
                    "The provider key could not be removed",
                  )}
                />
              )}
              {route === "keys" && <ApiKeysPage keys={data.apiKeys} busy={busy} onCreate={handleCreateKey} onRevoke={handleRevokeKey} />}
              {route === "usage" && <UsagePage events={data.usage} buckets={recentBuckets(data.usageBuckets)} />}
              {route === "billing" && <BillingPage wallet={data.wallet} busy={busy} onCheckout={handleCheckout} />}
            </>
          ) : <EmptyState title="Dashboard data is unavailable" description="Your dashboard data could not be loaded right now. Refresh to try again." />}
        </div>
      </main>

      {newKey && <SecretDialog apiKey={newKey} onClose={() => setNewKey(null)} />}
    </div>
  );
}

function Overview({data}: {data: DashboardData}) {
  const buckets = data.viewerUsage.map((bucket) => ({...bucket, spendMicros: bucket.chargeMicros}));
  return (
    <div className="page-stack">
      <section className="panel usage-chart-panel">
        <div className="panel-heading"><div><h2>Token usage</h2><p>Daily input and output tokens by model over the last four weeks.</p></div></div>
        <UsageChart buckets={buckets} ariaLabel="Daily personal token usage by model over the last 28 days" />
      </section>
      <div className="overview-chart-grid">
        <section className="panel metric-chart-panel">
          <div className="panel-heading"><div><h2>Requests</h2><p>Daily inference requests.</p></div></div>
          <MetricChart buckets={buckets} value={(bucket) => bucket.requests} formatValue={formatNumber} ariaLabel="Daily personal inference requests" />
        </section>
        <section className="panel metric-chart-panel">
          <div className="panel-heading"><div><h2>Spend</h2><p>Daily settled inference charges.</p></div></div>
          <MetricChart buckets={buckets} value={(bucket) => bucket.spendMicros} formatValue={formatDollars} ariaLabel="Daily personal inference spend" money />
        </section>
      </div>
    </div>
  );
}

const CHART_COLORS = ["#7a71ff", "#43d9c0", "#67d69a", "#e8b35f", "#ef8094", "#58a6ff"];
const CHART_OTHER_COLOR = "#4a4a52";
const CHART_SPEND_COLOR = "#f4f2ff";
const CHART_SPEND_CASING = "#0b0b0c";
const chartFrame = {width: 1120, height: 300, top: 18, right: 64, bottom: 32, left: 56};

interface ChartDay {
  key: string;
  label: string;
  tokensByModel: Map<string, number>;
  totalTokens: number;
  spendMicros: number;
}

function dayKey(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function chartCeil(value: number): number {
  if (value <= 0) return 1;
  const power = 10 ** Math.floor(Math.log10(value));
  const unit = value / power;
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 5 ? 5 : 10) * power;
}

function compactNumber(value: number): string {
  const units: Array<[number, string]> = [[1e9, "B"], [1e6, "M"], [1e3, "k"]];
  for (const [factor, suffix] of units) {
    if (value >= factor) {
      const scaled = value / factor;
      return `${scaled >= 100 || Number.isInteger(scaled) ? Math.round(scaled) : scaled.toFixed(1)}${suffix}`;
    }
  }
  return String(Math.round(value));
}

function axisDollars(micros: number): string {
  const dollars = micros / 1_000_000;
  if (dollars >= 1000) return `$${compactNumber(dollars)}`;
  return `$${dollars.toFixed(dollars > 0 && dollars < 10 ? 2 : 0)}`;
}

type ChartUsageBucket = {
  hour: string;
  model: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  spendMicros: number;
};

function UsageChart({buckets, ariaLabel}: {buckets: ChartUsageBucket[]; ariaLabel: string}) {
  const [hover, setHover] = useState<number | null>(null);
  const {width, height, top, right, bottom, left} = chartFrame;
  const innerWidth = width - left - right;
  const innerHeight = height - top - bottom;

  const plan = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days: ChartDay[] = [];
    const byKey = new Map<string, ChartDay>();
    for (let offset = 27; offset >= 0; offset -= 1) {
      const date = new Date(today.getTime() - offset * 86_400_000);
      const day: ChartDay = {
        key: dayKey(date),
        label: new Intl.DateTimeFormat("en", {month: "short", day: "numeric"}).format(date),
        tokensByModel: new Map(),
        totalTokens: 0,
        spendMicros: 0,
      };
      days.push(day);
      byKey.set(day.key, day);
    }
    for (const bucket of buckets) {
      const day = byKey.get(dayKey(new Date(bucket.hour)));
      if (!day) continue;
      const tokens = bucket.inputTokens + bucket.outputTokens;
      day.tokensByModel.set(bucket.model, (day.tokensByModel.get(bucket.model) ?? 0) + tokens);
      day.totalTokens += tokens;
      day.spendMicros += bucket.spendMicros;
    }
    const totals = new Map<string, number>();
    for (const day of days) {
      for (const [model, tokens] of day.tokensByModel) totals.set(model, (totals.get(model) ?? 0) + tokens);
    }
    const ranked = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([model]) => model);
    return {
      days,
      models: ranked.slice(0, CHART_COLORS.length),
      otherModels: ranked.slice(CHART_COLORS.length),
      maxTokens: chartCeil(Math.max(1, ...days.map((day) => day.totalTokens))),
    };
  }, [buckets]);

  if (!buckets.length) {
    return <EmptyState title="No usage yet" description="Daily usage will appear here after the first request settles." />;
  }

  const {days, models, otherModels, maxTokens} = plan;
  const slot = innerWidth / days.length;
  const barWidth = Math.min(slot * 0.6, 26);
  const tokenY = (tokens: number) => top + innerHeight * (1 - tokens / maxTokens);
  const centerX = (index: number) => left + slot * (index + 0.5);
  const hoveredDay = hover == null ? undefined : days[hover];

  const locate = (event: MouseEvent<SVGRectElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = ((event.clientX - bounds.left) / bounds.width) * innerWidth;
    setHover(Math.max(0, Math.min(days.length - 1, Math.floor(position / slot))));
  };

  return (
    <div className="chart-body">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={ariaLabel}>
        {Array.from({length: 5}, (_, tick) => {
          const y = top + innerHeight * (1 - tick / 4);
          return (
            <g key={tick}>
              <line x1={left} x2={width - right} y1={y} y2={y} stroke="#17171a" />
              <text x={left - 10} y={y} textAnchor="end" dominantBaseline="central" fontSize={10} fill="#66666f">{compactNumber((maxTokens * tick) / 4)}</text>
            </g>
          );
        })}
        {days.map((day, index) => {
          const stack = models.flatMap((model, colorIndex) => {
            const tokens = day.tokensByModel.get(model) ?? 0;
            return tokens > 0 ? [{fill: CHART_COLORS[colorIndex], tokens}] : [];
          });
          const otherTokens = otherModels.reduce((sum, model) => sum + (day.tokensByModel.get(model) ?? 0), 0);
          if (otherTokens > 0) stack.push({fill: CHART_OTHER_COLOR, tokens: otherTokens});
          const x = left + slot * index + (slot - barWidth) / 2;
          let consumed = 0;
          return (
            <g key={day.key}>
              {stack.map((segment) => {
                const segmentHeight = Math.max((innerHeight * segment.tokens) / maxTokens, 2);
                const y = top + innerHeight - consumed - segmentHeight;
                consumed += segmentHeight;
                return <rect key={segment.fill} x={x} y={y} width={barWidth} height={segmentHeight} fill={segment.fill} />;
              })}
            </g>
          );
        })}
        {days.map((day, index) => (index % 7 === 0 || index === days.length - 1) && (
          <text key={`x-${day.key}`} x={centerX(index)} y={height - 9} textAnchor="middle" fontSize={10} fill="#66666f">{day.label}</text>
        ))}
        {hoveredDay && hover != null && (
          <g pointerEvents="none">
            <rect x={left + slot * hover} y={top} width={slot} height={innerHeight} fill="#ffffff" opacity={0.05} />
          </g>
        )}
        <rect x={left} y={top} width={innerWidth} height={innerHeight} fill="transparent" onMouseMove={locate} onMouseLeave={() => setHover(null)} />
      </svg>
      {hoveredDay && hover != null && (() => {
        const rows = models
          .map((model, index) => ({label: model, fill: CHART_COLORS[index], tokens: hoveredDay.tokensByModel.get(model) ?? 0}))
          .filter((row) => row.tokens > 0);
        const otherTokens = otherModels.reduce((sum, model) => sum + (hoveredDay.tokensByModel.get(model) ?? 0), 0);
        if (otherTokens > 0) rows.push({label: "Other", fill: CHART_OTHER_COLOR, tokens: otherTokens});
        const anchorTop = Math.max(top, tokenY(hoveredDay.totalTokens));
        const anchorLeft = Math.min(88, Math.max(12, (100 * centerX(hover)) / width));
        return (
          <div className="chart-tooltip" style={{left: `${anchorLeft}%`, top: `${(100 * anchorTop) / height}%`}}>
            <strong>{hoveredDay.label}</strong>
            {rows.length ? rows.map((row) => (
              <span key={row.label}><i style={{background: row.fill}} />{row.label}<b>{compactNumber(row.tokens)}</b></span>
            )) : <span>No requests</span>}
          </div>
        );
      })()}
      <div className="chart-legend">
        {[...models.map((model, index) => ({label: model, fill: CHART_COLORS[index]})),
          ...(otherModels.length ? [{label: "Other", fill: CHART_OTHER_COLOR}] : []),
        ].map((entry) => <span key={entry.label}><i style={{background: entry.fill}} />{entry.label}</span>)}
      </div>
    </div>
  );
}

function MetricChart({buckets, value, formatValue, ariaLabel, money = false}: {
  buckets: ChartUsageBucket[];
  value: (bucket: ChartUsageBucket) => number;
  formatValue: (value: number) => string;
  ariaLabel: string;
  money?: boolean;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const frame = {width: 560, height: 220, top: 16, right: 18, bottom: 30, left: 48};
  const innerWidth = frame.width - frame.left - frame.right;
  const innerHeight = frame.height - frame.top - frame.bottom;
  const days = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const result = Array.from({length: 28}, (_, index) => {
      const date = new Date(today.getTime() - (27 - index) * 86_400_000);
      return {
        key: dayKey(date),
        label: new Intl.DateTimeFormat("en", {month: "short", day: "numeric"}).format(date),
        value: 0,
      };
    });
    const byKey = new Map(result.map((day) => [day.key, day]));
    for (const bucket of buckets) {
      const day = byKey.get(dayKey(new Date(bucket.hour)));
      if (day) day.value += value(bucket);
    }
    return result;
  }, [buckets, value]);

  if (!buckets.length) {
    return <EmptyState title="No usage yet" description="This chart will populate after the first request settles." />;
  }

  const maxValue = chartCeil(Math.max(1, ...days.map((day) => day.value)));
  const slot = innerWidth / Math.max(1, days.length - 1);
  const x = (index: number) => frame.left + slot * index;
  const y = (metric: number) => frame.top + innerHeight * (1 - metric / maxValue);
  const points = days.map((day, index) => `${x(index)},${y(day.value)}`).join(" ");
  const hoveredDay = hover === null ? undefined : days[hover];
  const locate = (event: MouseEvent<SVGRectElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = ((event.clientX - bounds.left) / bounds.width) * innerWidth;
    setHover(Math.max(0, Math.min(days.length - 1, Math.round(position / slot))));
  };

  return (
    <div className="chart-body metric-chart-body">
      <svg viewBox={`0 0 ${frame.width} ${frame.height}`} role="img" aria-label={ariaLabel}>
        {Array.from({length: 4}, (_, tick) => {
          const metric = (maxValue * tick) / 3;
          const tickY = y(metric);
          return (
            <g key={tick}>
              <line x1={frame.left} x2={frame.width - frame.right} y1={tickY} y2={tickY} stroke="#17171a" />
              <text x={frame.left - 9} y={tickY} textAnchor="end" dominantBaseline="central" fontSize={9} fill="#66666f">
                {money ? axisDollars(metric) : compactNumber(metric)}
              </text>
            </g>
          );
        })}
        <polygon
          points={`${frame.left},${frame.top + innerHeight} ${points} ${frame.width - frame.right},${frame.top + innerHeight}`}
          fill="#5348f2"
          opacity={0.09}
        />
        <g fill="none" strokeLinecap="round" strokeLinejoin="round">
          <polyline points={points} stroke={CHART_SPEND_CASING} strokeWidth={5} opacity={0.9} />
          <polyline points={points} stroke={CHART_SPEND_COLOR} strokeWidth={1.6} opacity={0.96} />
        </g>
        {days.map((day, index) => (index % 7 === 0 || index === days.length - 1) && (
          <text key={day.key} x={x(index)} y={frame.height - 9} textAnchor="middle" fontSize={9} fill="#66666f">{day.label}</text>
        ))}
        {hoveredDay && hover !== null && (
          <g pointerEvents="none">
            <line x1={x(hover)} x2={x(hover)} y1={frame.top} y2={frame.top + innerHeight} stroke="#ffffff" opacity={0.12} />
            <circle cx={x(hover)} cy={y(hoveredDay.value)} r={3.2} fill={CHART_SPEND_COLOR} stroke={CHART_SPEND_CASING} strokeWidth={1.5} />
          </g>
        )}
        <rect x={frame.left} y={frame.top} width={innerWidth} height={innerHeight} fill="transparent" onMouseMove={locate} onMouseLeave={() => setHover(null)} />
      </svg>
      {hoveredDay && hover !== null && (
        <div className="chart-tooltip metric-tooltip" style={{left: `${Math.min(86, Math.max(14, (100 * x(hover)) / frame.width))}%`, top: `${(100 * y(hoveredDay.value)) / frame.height}%`}}>
          <strong>{hoveredDay.label}</strong>
          <span><i style={{background: CHART_SPEND_COLOR}} />Total<b>{formatValue(hoveredDay.value)}</b></span>
        </div>
      )}
    </div>
  );
}

// A provider serves a model on the account's own key once it carries a working
// key and a mode that prefers it; auto counts because it tries that key first.
export function byokServedModelIds(
  models: DashboardData["models"],
  settings: ProviderSetting[],
): Set<string> {
  const active = new Set(settings
    .filter((setting) => setting.credential?.status === "active" && (setting.mode === "byok" || setting.mode === "auto"))
    .map((setting) => setting.provider));
  return new Set(models
    .filter((model) => model.providers.some((provider) => active.has(provider)))
    .map((model) => model.id));
}

function ModelsPage({models, performance, settings, byokEnabled}: {
  models: DashboardData["models"];
  performance: DashboardData["modelPerformance"];
  settings: DashboardData["providerSettings"];
  byokEnabled: boolean;
}) {
  const [byokOnly, setByokOnly] = useState(false);
  const byokModels = useMemo(() => byokServedModelIds(models, settings), [models, settings]);
  const onlyModelIds = byokEnabled && byokOnly ? byokModels : null;
  return <section className="panel page-panel"><div className="panel-heading"><div><h2>Models</h2><p>Seven-day performance by stable Hashset model ID, regardless of provider.</p></div>{byokEnabled && <Checkbox checked={byokOnly} onChange={setByokOnly}>BYOK only</Checkbox>}</div><ModelPerformanceTable models={models} rows={performance} onlyModelIds={onlyModelIds} /></section>;
}

function ModelPerformanceTable({models, rows, onlyModelIds = null}: {
  models: DashboardData["models"];
  rows: DashboardData["modelPerformance"];
  onlyModelIds?: Set<string> | null;
}) {
  const performanceByModel = new Map(rows.map((row) => [row.model, row]));
  const logicalRows = [...new Set([...models.map((model) => model.id), ...rows.map((row) => row.model)])]
    .filter((model) => !onlyModelIds || onlyModelIds.has(model))
    .sort()
    .map((model) => performanceByModel.get(model) ?? {
      model,
      errorRate: null,
      averageTtfbMs: null,
      tokensPerSecond: null,
      volumeTokens: 0,
    });
  if (!logicalRows.length) {
    return onlyModelIds
      ? <EmptyState title="No models on your own key" description="None of the running models are served by a provider you have connected with your own key." />
      : <EmptyState title="No models are running" description="Models will appear when Hashset publishes inference capacity." />;
  }
  return <div className="performance-scroll"><div className="performance-table"><div className="performance-head"><span>Model</span><span>Avg latency</span><span>Volume</span><span>Error rate</span></div>{logicalRows.map((row) => <div className="performance-row" key={row.model}><strong>{row.model}</strong><span>{row.averageTtfbMs === null ? "—" : `${Math.round(row.averageTtfbMs)} ms`}</span><span>{compactNumber(row.volumeTokens)}</span><span className={row.errorRate !== null && row.errorRate <= .01 ? "metric-good" : ""}>{row.errorRate === null ? "—" : `${(row.errorRate * 100).toFixed(row.errorRate < .005 ? 1 : 2)}%`}</span></div>)}</div></div>;
}

function ModelRows({models, detailed = false}: {models: DashboardData["models"]; detailed?: boolean}) {
  if (!models.length) return <EmptyState title="No models are running" description="Register a healthy GPU cluster to publish its models to the router." />;
  return <div className="model-list">{models.map((model) => (
    <div className="model-row" key={model.id}>
      <span className={`model-status ${model.status}`} />
      <div className="model-identity"><strong>{model.id}</strong><span>{model.clusterName} · {model.region}</span></div>
      {detailed && <><div className="model-price"><span>Input</span><strong>{formatDollars(model.inputPriceMicrosPerMillion)} / 1M</strong></div><div className="model-price"><span>Output</span><strong>{formatDollars(model.outputPriceMicrosPerMillion)} / 1M</strong></div></>}
      <div className="model-seen"><span className={`status-label ${model.status}`}>{model.status}</span><small>{formatDate(model.lastSeenAt)}</small></div>
    </div>
  ))}</div>;
}

const providerWarnings: Record<string, string> = {
  insufficient_provider_funds: "The key works, but the provider account has no funds. Top it up before routing traffic.",
  provider_permission_denied: "The key works, but the provider reports limited permissions for it.",
};

const providerReasons: Record<string, string> = {
  provider_disabled: "Paused on Hashset, so nothing routes here right now",
  mode_off: "Switched off for this account",
  byok_unavailable: "Your own key is no longer available on this account",
  credential_missing: "Add your provider key to start serving",
  credential_invalid: "The provider rejected this key. Enter it again.",
  requires_own_key: "Switch to your own key to serve this provider",
};

const attentionLabels: Record<ProviderAttention, string> = {
  unentitled: "Remove only",
  invalid: "Key rejected",
  incomplete: "Key missing",
};

export type ProviderChangeOutcome =
  | {ok: true; warning: string | null}
  | {ok: false; error: string};

// The key form clears its inputs on the result of this call, so a failed save
// must be reported as failure rather than resolved: clearing on a provider or
// sync error would force the customer to retype the secret.
export async function providerChangeOutcome(
  change: () => Promise<unknown>,
  failure: string,
): Promise<ProviderChangeOutcome> {
  try {
    const warning = ((await change()) as {warning?: string | null} | null)?.warning;
    return {ok: true, warning: warning ? providerWarnings[warning] ?? warning : null};
  } catch (cause) {
    return {ok: false, error: cause instanceof Error ? cause.message : failure};
  }
}

export const BYOK_ENABLE_FAILED_NOTE =
  "Enabling your own key failed, so this provider stayed on its previous mode. "
  + "Switch it on the provider card.";

// Connecting a provider means serving on it, so the account is moved onto the
// new key straight away. The key is stored before that flip, so a refused flip
// is not a failed save — reporting it as one would ask for a secret Hashset
// already holds. It is not silent either: the reason comes back as a warning,
// because a dialog that closes on "Key saved" while the provider sits on
// platform or excluded tells the customer the opposite of what happened.
export async function addProviderKeyChange(
  saveKey: () => Promise<unknown>,
  enableByok: () => Promise<unknown>,
): Promise<{warning: string | null}> {
  const saved = (await saveKey()) as {warning?: string | null} | null;
  try {
    await enableByok();
  } catch (cause) {
    const reason = cause instanceof Error ? cause.message : null;
    return {warning: reason ? `${BYOK_ENABLE_FAILED_NOTE} ${reason}` : BYOK_ENABLE_FAILED_NOTE};
  }
  return {warning: saved?.warning ?? null};
}

export type AddProviderResult = {close: boolean; error: string | null};

// A save that failed leaves the form open with the typed key intact and the
// reason beside it: the page notice behind the modal is invisible from there.
export function addProviderResult(outcome: ProviderChangeOutcome): AddProviderResult {
  return outcome.ok ? {close: true, error: null} : {close: false, error: outcome.error};
}

export type DialogDismissal = "close" | "hold";

// A dialog running the call it was opened for has to survive it: the failure it
// may have to report renders inside the dialog, and the page notice behind the
// modal is invisible from there. Escape, the backdrop and the close button all
// route through this so none of them can drop the answer on the floor.
export function dialogDismissal(pending: boolean): DialogDismissal {
  return pending ? "hold" : "close";
}

export const AUTO_MODE_NOTE =
  "Uses your key first. If your key stops working, requests fall back to Hashset's key "
  + "and are billed to your Hashset balance.";

// Auto is the one mode that can move a provider's requests onto the Hashset
// balance without another action, so it is confirmed the way key removal is
// rather than explained only in a tip the customer may never open.
export const AUTO_MODE_CONSENT =
  "Auto uses your key first. If your key stops working, requests fall back to Hashset's key "
  + "and are billed to your Hashset balance. Continue?";

export const AUTO_FALLBACK_LOCKED_NOTE =
  "Your own key is no longer available here. Requests are served on Hashset's key "
  + "and billed to your balance.";

export const BYOK_LOCKED_NOTE = "Your own key is no longer available here.";

// A withdrawn entitlement does not stop an auto provider — it keeps serving on
// Hashset's key, which the wallet pays for — so the card says so instead of
// reporting only that the key is gone.
export function providerLockedNote(setting: ProviderSetting): string {
  return setting.mode === "auto" && setting.platformAllowed ? AUTO_FALLBACK_LOCKED_NOTE : BYOK_LOCKED_NOTE;
}

// Auto is byok plus a platform fallback on the same provider, so it needs both
// halves to exist before it can be offered.
export function providerAutoAvailable(setting: ProviderSetting): boolean {
  return setting.byokAvailable && setting.platformAllowed;
}

// The control API refuses byok and auto until a working key is stored, so the
// card must not offer them before there is one: a selectable mode that answers
// with credential_required asks for a decision the account cannot carry out.
export function providerKeyReady(setting: ProviderSetting): boolean {
  return setting.credential?.status === "active";
}

export type ProviderModeOption = {
  value: ProviderMode;
  label: string;
  disabled?: boolean;
  title?: string;
};

export function providerModeOptions(setting: ProviderSetting): ProviderModeOption[] {
  const ready = providerKeyReady(setting);
  // A mode the account currently sits on always stays listed, unselectable, so
  // the control never shows a mode the account is not in: a BYOK-only provider
  // still resolves an unset account to platform, and an entitlement can be
  // withdrawn under an account already on byok or auto.
  const option = (value: ProviderMode, label: string, available: boolean, needsKey = false) => {
    if (!available) return setting.mode === value ? [{value, label, disabled: true}] : [];
    if (needsKey && !ready) return [{value, label, disabled: true, title: `Add a working key to use ${label}`}];
    return [{value, label}];
  };
  return [
    {value: "off" as const, label: "Off"},
    ...option("platform", "Hashset", setting.platformAllowed),
    ...option("byok", "BYOK", setting.byokAvailable, true),
    ...option("auto", "Auto", providerAutoAvailable(setting), true),
  ];
}

export type ConfirmPrompt = {
  title: string;
  body: string;
  confirmLabel: string;
  pendingLabel: string;
  tone: "danger" | "primary";
};

export const AUTO_MODE_PROMPT: ConfirmPrompt = {
  title: "Auto falls back to Hashset’s key",
  body: AUTO_MODE_CONSENT,
  confirmLabel: "Enable Auto",
  pendingLabel: "Enabling…",
  tone: "primary",
};

export function removeKeyPrompt(setting: ProviderSetting): ConfirmPrompt {
  return {
    title: `Remove your ${setting.name} key?`,
    body: `${setting.name} switches off for this account and stops serving requests immediately.`,
    confirmLabel: "Remove key",
    pendingLabel: "Removing…",
    tone: "danger",
  };
}

export const REVOKE_API_KEY_PROMPT: ConfirmPrompt = {
  title: "Revoke this API key?",
  body: "Requests using it will stop immediately.",
  confirmLabel: "Revoke key",
  pendingLabel: "Revoking…",
  tone: "danger",
};

export type ProviderModeChoice =
  | {action: "ignore"}
  | {action: "confirm"; prompt: ConfirmPrompt}
  | {action: "apply"};

// Auto is the one mode that can move a provider's requests onto the Hashset
// balance without another action, so it is confirmed before it is applied. The
// mode is not sent while the prompt is open, which leaves the control showing
// the mode the account is still in if the prompt is declined.
export function providerModeChoice(setting: ProviderSetting, mode: ProviderMode): ProviderModeChoice {
  if (mode === setting.mode) return {action: "ignore"};
  if (mode === "auto") return {action: "confirm", prompt: AUTO_MODE_PROMPT};
  return {action: "apply"};
}

export type ProviderAttention = "unentitled" | "invalid" | "incomplete";

// Ordered by what the customer can still do about it: a withdrawn entitlement
// leaves nothing but removal, a rejected key can be replaced, and a missing one
// is only half-configured.
export function providerAttention(setting: ProviderSetting): ProviderAttention | null {
  if (setting.credential && !setting.byokAvailable) return "unentitled";
  if (setting.credential?.status === "invalid") return "invalid";
  // Auto keeps serving on Hashset's key without one, but the account asked for
  // its own key to be preferred, so a missing key is still half-configured.
  if ((setting.mode === "byok" || setting.mode === "auto") && !setting.credential) return "incomplete";
  return null;
}

// A stored key outlives its entitlement, so presence alone keeps a provider on
// the page: hiding it would strand the secret with no way to delete it.
export function isProviderConfigured(setting: ProviderSetting): boolean {
  if (setting.credential) return true;
  // An explicit `off` without a stored key is what a removed provider looks
  // like (deletion revokes the key and parks the mode on `off`), so it goes
  // back to the Add pool instead of lingering as an empty card.
  return setting.byokAvailable && ["byok", "auto"].includes(setting.mode);
}

export function visibleProviders(settings: ProviderSetting[]): ProviderSetting[] {
  return settings
    .filter(isProviderConfigured)
    .sort((left, right) =>
      Number(providerAttention(left) === null) - Number(providerAttention(right) === null)
      || left.name.localeCompare(right.name));
}

export function addableProviders(settings: ProviderSetting[]): ProviderSetting[] {
  return settings
    .filter((setting) => setting.byokAvailable && !isProviderConfigured(setting))
    .sort((left, right) => left.name.localeCompare(right.name));
}

export function byokEntitled(settings: ProviderSetting[]): boolean {
  return settings.some((setting) => setting.byokAvailable);
}

export function providerCounts(settings: ProviderSetting[]): {serving: number; attention: number; idle: number} {
  const counts = {serving: 0, attention: 0, idle: 0};
  for (const setting of visibleProviders(settings)) {
    if (providerAttention(setting)) counts.attention += 1;
    else if (setting.effectiveState === "active") counts.serving += 1;
    else counts.idle += 1;
  }
  return counts;
}

function ProvidersPage({settings, busy, onMode, onSaveKey, onAddKey, onDeleteKey}: {
  settings: DashboardData["providerSettings"];
  busy: boolean;
  onMode: (provider: string, mode: ProviderMode) => Promise<boolean>;
  onSaveKey: (provider: string, key: string, attested: boolean) => Promise<ProviderChangeOutcome>;
  onAddKey: (provider: string, key: string, attested: boolean) => Promise<ProviderChangeOutcome>;
  onDeleteKey: (provider: string) => Promise<boolean>;
}) {
  const [picking, setPicking] = useState(false);
  const visible = useMemo(() => visibleProviders(settings), [settings]);
  const addable = useMemo(() => addableProviders(settings), [settings]);
  const entitled = byokEntitled(settings);
  const counts = providerCounts(settings);
  const exhausted = entitled && !addable.length;

  return <div className="byok-page">
    <section className="byok-masthead">
      <div className="byok-masthead-copy">
        <span className="byok-eyebrow">Bring your own key</span>
        <h2>Providers served on keys you own</h2>
        <p>
          A key is encrypted the moment it arrives and never shown again. While a provider runs on
          your key, its tokens are billed by the provider and never against your Hashset balance.
        </p>
      </div>
      <div className="byok-masthead-side">
        {visible.length > 0 && <div className="byok-stats">
          <span className="byok-stat" data-tone="serving"><i />
            <b>{counts.serving}</b> serving
          </span>
          {counts.attention > 0 && <span className="byok-stat" data-tone="attention"><i />
            <b>{counts.attention}</b> need attention
          </span>}
          {counts.idle > 0 && <span className="byok-stat" data-tone="idle"><i />
            <b>{counts.idle}</b> off
          </span>}
        </div>}
        {entitled && <button className="primary small" disabled={busy || exhausted}
          title={exhausted ? "Every provider that supports your own key already has one." : undefined}
          onClick={() => setPicking(true)}>
          <Icon name="plus" />Add provider
        </button>}
      </div>
    </section>

    {visible.length ? <div className="provider-grid">
      {visible.map((setting, index) => (
        <ProviderCard key={setting.provider} setting={setting} index={index} busy={busy}
          onMode={onMode} onSaveKey={onSaveKey} onDeleteKey={onDeleteKey} />
      ))}
    </div> : entitled ? <section className="panel">
      <div className="byok-hero">
        <span className="byok-hero-glyph"><Icon name="key" /></span>
        <h3>No provider keys connected</h3>
        <p>
          Connect a key and Hashset routes that provider’s models through your own account. Same
          endpoint, same model IDs, and your own billing relationship with the provider.
        </p>
        <button className="primary small" disabled={busy || exhausted} onClick={() => setPicking(true)}>
          <Icon name="plus" />Add provider
        </button>
      </div>
    </section> : <section className="panel">
      <div className="byok-hero" data-locked="">
        <span className="byok-hero-glyph"><Icon name="lock" /></span>
        <h3>Bring your own key isn’t enabled for this account</h3>
        <p>
          Every provider is served on Hashset’s own keys right now. Once BYOK is enabled here,
          this page is where you connect and retire your provider keys.
        </p>
      </div>
    </section>}

    {picking && <ProviderKeyDialog providers={addable} chosenProvider={null} busy={busy}
      onClose={() => setPicking(false)}
      onSave={(provider, key, attested) => onAddKey(provider, key, attested).then((outcome) => {
        const result = addProviderResult(outcome);
        if (result.close) setPicking(false);
        return result;
      })} />}
  </div>;
}

function ProviderCard({setting, index, busy, onMode, onSaveKey, onDeleteKey}: {
  setting: ProviderSetting;
  index: number;
  busy: boolean;
  onMode: (provider: string, mode: ProviderMode) => Promise<boolean>;
  onSaveKey: (provider: string, key: string, attested: boolean) => Promise<ProviderChangeOutcome>;
  onDeleteKey: (provider: string) => Promise<boolean>;
}) {
  const [replacing, setReplacing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState<{prompt: ConfirmPrompt; run: () => Promise<unknown>} | null>(null);

  const attention = providerAttention(setting);
  const serving = setting.effectiveState === "active";
  const tone = attention ? "attention" : serving ? "serving" : "idle";
  const reason = setting.effectiveReason ? providerReasons[setting.effectiveReason] ?? setting.effectiveReason : null;
  const modeOptions = providerModeOptions(setting);
  const selected = modeOptions.findIndex((option) => option.value === setting.mode);
  const keyBlocked = modeOptions.filter((option) => option.title).map((option) => option.label);
  // A missing or rejected key is the one thing blocking this provider, so its
  // form is the card's body rather than something behind another click.
  const prompting = setting.byokAvailable && setting.credential?.status !== "active";

  function chooseMode(mode: ProviderMode) {
    const choice = providerModeChoice(setting, mode);
    if (choice.action === "ignore") return;
    if (choice.action === "apply") {
      void onMode(setting.provider, mode);
      return;
    }
    setConfirming({prompt: choice.prompt, run: () => onMode(setting.provider, mode)});
  }

  return <article className="provider-card" data-tone={tone} style={{"--index": index} as CSSProperties}>
    <div className="provider-card-head">
      <div className="provider-title">
        <strong>{setting.name}</strong>
        <code>{setting.provider}</code>
      </div>
      <span className="state-pill" data-tone={tone}>
        <i />{attention ? attentionLabels[attention] : serving ? "Serving" : "Excluded"}
      </span>
    </div>

    {reason && <p className="provider-reason" data-tone={tone}>
      <Icon name={attention ? "alert" : "info"} />{reason}
    </p>}

    <div className="key-plate" data-empty={setting.credential ? undefined : ""}>
      <div className="key-plate-row">
        <span className="micro-label">Key</span>
        {setting.credential ? <>
          <code>···{setting.credential.hint}</code>
          <span className="credential-state" data-status={setting.credential.status}>
            {setting.credential.status === "active" ? "Active" : "Rejected"}
          </span>
        </> : <code data-none="">Nothing stored</code>}
      </div>
      {setting.credential && <small>Added {formatDay(setting.credential.createdAt)}</small>}
    </div>

    <div className="provider-mode">
      <span className="micro-label">Mode</span>
      <div className="mode-row">
        <div className="mode-switch" role="radiogroup" aria-label={`${setting.name} mode`}
          data-unset={selected < 0 ? "" : undefined}
          style={{"--mode-count": modeOptions.length, "--mode-index": Math.max(selected, 0)} as CSSProperties}>
          <span className="mode-thumb" aria-hidden="true" />
          {modeOptions.map((option) => (
            <button key={option.value} type="button" role="radio" aria-checked={option.value === setting.mode}
              disabled={busy || option.disabled || !setting.enabled} title={option.title}
              onClick={() => chooseMode(option.value)}>
              {option.label}
            </button>
          ))}
        </div>
        {/* Outside the radiogroup: a button among the radios would be announced
            as one of the modes. */}
        {providerAutoAvailable(setting) && <InfoTip label="What the Auto mode does" text={AUTO_MODE_NOTE} />}
      </div>
      {selected < 0 && <small className="mode-note">Currently served on Hashset’s key.</small>}
      {/* A disabled button shows no tooltip in most browsers, so the reason the
          key modes are greyed out is spelled out under the switch as well. */}
      {keyBlocked.length > 0 && (
        <small className="mode-note">Add a working key to use {keyBlocked.join(" or ")}.</small>
      )}
    </div>

    {prompting && <ProviderKeyForm className="key-form" provider={setting} busy={busy} pending={saving}
      onPending={setSaving}
      onSave={(key, attested) => onSaveKey(setting.provider, key, attested).then(addProviderResult)} />}

    {(!prompting || !setting.byokAvailable || setting.credential) && <div className="provider-actions">
      {setting.byokAvailable && !prompting && <button type="button" className="ghost-button" disabled={busy}
        onClick={() => setReplacing(true)}><Icon name="key" />Replace key</button>}
      {/* The stored key outlives the entitlement, so removal stays reachable
          even once no new key can be saved. */}
      {!setting.byokAvailable && <span className="provider-locked">{providerLockedNote(setting)}</span>}
      {setting.credential && <button className="danger-button" disabled={busy}
        onClick={() => setConfirming({prompt: removeKeyPrompt(setting), run: () => onDeleteKey(setting.provider)})}>
        <Icon name="trash" />Remove
      </button>}
    </div>}

    {replacing && <ProviderKeyDialog providers={[]} chosenProvider={setting} busy={busy}
      onClose={() => setReplacing(false)}
      onSave={(provider, key, attested) => onSaveKey(provider, key, attested).then((outcome) => {
        const result = addProviderResult(outcome);
        if (result.close) setReplacing(false);
        return result;
      })} />}

    {confirming && <ConfirmDialog prompt={confirming.prompt} busy={busy}
      onDismiss={() => setConfirming(null)} onConfirm={confirming.run} />}
  </article>;
}

// The native control cannot be styled, so the real checkbox stays for the
// keyboard and the accessibility tree while the box beside it is drawn from
// :checked and :focus-visible alone: no second copy of the state to keep.
function Checkbox({checked, disabled, onChange, children}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return <label className="checkbox">
    <input type="checkbox" checked={checked} disabled={disabled}
      onChange={(event) => onChange(event.target.checked)} />
    <span className="checkbox-box" aria-hidden="true"><Icon name="check" /></span>
    <span className="checkbox-copy">{children}</span>
  </label>;
}

export function providerKeyNote(setting: ProviderSetting): string {
  return setting.credential
    ? `Paste a new key from your ${setting.name} account. It replaces the stored key as soon as it is saved, `
      + `and the key it replaces stops being used.`
    : `Paste a key from your ${setting.name} account. Hashset encrypts it on arrival, never displays it again, `
      + `and starts routing ${setting.name} models through it for this account.`;
}

// One form serves every entrance to a provider key: the picker's second step,
// the replace dialog, and the card of a provider that is blocked on a missing
// or rejected key. The caller owns the pending flag because a dialog has to
// know when its save is in flight to stop itself being dismissed.
function ProviderKeyForm({provider, className, note, busy, pending, autoFocus, secondary, onPending, onSave}: {
  provider: ProviderSetting;
  className: string;
  note?: string;
  busy: boolean;
  pending: boolean;
  autoFocus?: boolean;
  secondary?: ReactNode;
  onPending: (pending: boolean) => void;
  onSave: (key: string, attested: boolean) => Promise<AddProviderResult>;
}) {
  const [key, setKey] = useState("");
  const [attested, setAttested] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  function submit(event: FormEvent) {
    event.preventDefault();
    if (pending || !key.trim() || !attested) return;
    setFailure(null);
    onPending(true);
    void onSave(key.trim(), attested).then((result) => {
      onPending(false);
      setFailure(result.error);
      if (!result.close) return;
      setKey("");
      setAttested(false);
    });
  }

  return <form className={className} onSubmit={submit}>
    {note && <p className="picker-note">{note}</p>}
    {/* Read-only rather than disabled while saving: a disabled field loses
        focus, and the key has to stay put for a retry. */}
    <input ref={inputRef} type="password" value={key} maxLength={512} autoComplete="off" spellCheck={false}
      readOnly={pending} onChange={(event) => setKey(event.target.value)}
      placeholder={provider.credential ? `Replace your ${provider.name} key` : `Your ${provider.name} key`}
      aria-label={`${provider.name} provider key`} />
    <Checkbox checked={attested} disabled={pending} onChange={setAttested}>
      I own this key and may use it for this account
    </Checkbox>
    {failure && <p className="picker-error" role="alert"><Icon name="alert" />{failure}</p>}
    <div className="key-form-actions">
      <button className="primary small" aria-busy={pending || undefined}
        disabled={busy || pending || !key.trim() || !attested}>
        {pending ? "Saving…" : "Save key"}
      </button>
      {secondary}
    </div>
  </form>;
}

export function providerKeyDialogTitle(chosen: ProviderSetting | null, replacing: boolean): string {
  if (!chosen) return "Add a provider";
  return replacing ? `Replace key for ${chosen.name}` : `Connect ${chosen.name}`;
}

// Opened on a provider it is the replace flow, opened on the list it is the add
// flow: the same key form either way, with the picker step in front of it only
// when there is something to pick.
function ProviderKeyDialog({providers, chosenProvider, busy, onClose, onSave}: {
  providers: ProviderSetting[];
  chosenProvider: ProviderSetting | null;
  busy: boolean;
  onClose: () => void;
  onSave: (provider: string, key: string, attested: boolean) => Promise<AddProviderResult>;
}) {
  const [chosen, setChosen] = useState<ProviderSetting | null>(chosenProvider);
  const [saving, setSaving] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const replacing = chosenProvider !== null;

  useEffect(() => {
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dialogDismissal(saving) === "close") onClose();
    };
    document.addEventListener("keydown", dismissOnEscape);
    return () => document.removeEventListener("keydown", dismissOnEscape);
  }, [saving, onClose]);

  useEffect(() => { if (!chosen) listRef.current?.querySelector("button")?.focus(); }, [chosen]);

  function dismiss() { if (dialogDismissal(saving) === "close") onClose(); }

  return <div className="dialog-backdrop" role="presentation"
    onMouseDown={(event) => { if (event.target === event.currentTarget) dismiss(); }}>
    <section className="picker-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <header className="picker-head">
        {chosen && !replacing && <button type="button" className="icon-button" aria-label="Back to the provider list"
          disabled={saving} onClick={() => setChosen(null)}><Icon name="back" /></button>}
        <h2 id={titleId}>{providerKeyDialogTitle(chosen, replacing)}</h2>
        {!replacing && <span className="picker-steps" aria-hidden="true">
          <i data-on="" /><i data-on={chosen ? "" : undefined} />
        </span>}
        <button type="button" className="icon-button" aria-label="Close" disabled={saving}
          onClick={dismiss}><Icon name="close" /></button>
      </header>
      <div className="picker-body">
        {chosen ? <ProviderKeyForm key={chosen.provider} className="picker-form" provider={chosen}
          note={providerKeyNote(chosen)} busy={busy} pending={saving} autoFocus onPending={setSaving}
          onSave={(key, attested) => onSave(chosen.provider, key, attested)}
          secondary={replacing
            ? <button type="button" className="ghost-button" disabled={saving} onClick={dismiss}>Cancel</button>
            : <button type="button" className="ghost-button" disabled={busy || saving}
              onClick={() => setChosen(null)}>Back</button>} />
          : providers.length ? <div className="picker-grid" ref={listRef}>
          {providers.map((provider, index) => (
            <button key={provider.provider} type="button" className="picker-tile"
              style={{"--index": index} as CSSProperties} onClick={() => setChosen(provider)}>
              <span className="picker-tile-mark"><Icon name="providers" /></span>
              <span className="picker-tile-copy">
                <strong>{provider.name}</strong>
                <small>{provider.platformAllowed ? "Also served on Hashset’s key" : "Only served on your own key"}</small>
              </span>
              <Icon name="forward" />
            </button>
          ))}
        </div> : <EmptyState title="Nothing left to connect"
          description="Every provider that supports your own key already has one on this account." />}
      </div>
    </section>
  </div>;
}

// window.confirm is suppressed without a trace by some browsers and embedders,
// which turns a destructive button into one that silently does nothing. The
// dialog is in-app so the question is always asked, and it holds itself open
// while the action it asked about is running.
function ConfirmDialog({prompt, busy, onDismiss, onConfirm}: {
  prompt: ConfirmPrompt;
  busy: boolean;
  onDismiss: () => void;
  onConfirm: () => Promise<unknown>;
}) {
  const [pending, setPending] = useState(false);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const bodyId = useId();
  const danger = prompt.tone === "danger";

  // A destructive prompt opens on its way out rather than on the button that
  // carries out the destruction.
  useEffect(() => { (danger ? cancelRef : confirmRef).current?.focus(); }, [danger]);

  useEffect(() => {
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && dialogDismissal(pending) === "close") onDismiss();
    };
    document.addEventListener("keydown", dismissOnEscape);
    return () => document.removeEventListener("keydown", dismissOnEscape);
  }, [pending, onDismiss]);

  function dismiss() { if (dialogDismissal(pending) === "close") onDismiss(); }

  function confirm() {
    if (pending) return;
    setPending(true);
    void onConfirm().finally(() => {
      setPending(false);
      onDismiss();
    });
  }

  return <div className="dialog-backdrop" role="presentation"
    onMouseDown={(event) => { if (event.target === event.currentTarget) dismiss(); }}>
    <section className="confirm-dialog" role="alertdialog" aria-modal="true"
      aria-labelledby={titleId} aria-describedby={bodyId} data-tone={prompt.tone}>
      <span className="confirm-glyph" aria-hidden="true"><Icon name={danger ? "alert" : "info"} /></span>
      <h2 id={titleId}>{prompt.title}</h2>
      <p id={bodyId}>{prompt.body}</p>
      <div className="confirm-actions">
        <button ref={cancelRef} type="button" className="ghost-button" disabled={pending}
          onClick={dismiss}>Cancel</button>
        <button ref={confirmRef} type="button" aria-busy={pending || undefined}
          className={danger ? "danger-button" : "primary small"} disabled={pending || busy}
          onClick={confirm}>
          {pending ? prompt.pendingLabel : prompt.confirmLabel}
        </button>
      </div>
    </section>
  </div>;
}

function ApiKeysPage({keys, busy, onCreate, onRevoke}: {keys: DashboardData["apiKeys"]; busy: boolean; onCreate: (name: string) => Promise<void>; onRevoke: (id: string) => Promise<void>}) {
  const [name, setName] = useState("");
  const [revoking, setRevoking] = useState<string | null>(null);
  return <div className="page-stack"><section className="panel create-key-panel"><div><h2>Create an API key</h2><p>The secret is shown once. Store it in your server-side environment.</p></div><form onSubmit={(event) => {event.preventDefault(); if (name.trim()) void onCreate(name.trim()).then(() => setName(""));}}><input value={name} maxLength={80} onChange={(event) => setName(event.target.value)} placeholder="Production" aria-label="API key name" /><button className="primary small" disabled={busy || !name.trim()}><Icon name="plus" />Create key</button></form></section><section className="panel page-panel"><div className="panel-heading"><h2>Your API keys</h2><span>{keys.filter((key) => !key.revokedAt).length} active</span></div>{keys.length ? <div className="key-list">{keys.map((key) => <div className="key-row" key={key.id}><div><strong>{key.name}</strong><code>{key.prefix}••••••••</code></div><span>{key.revokedAt ? "Revoked" : `Created ${formatDate(key.createdAt)}`}</span>{!key.revokedAt && <button className="danger-button" disabled={busy} onClick={() => setRevoking(key.id)}>Revoke</button>}</div>)}</div> : <EmptyState title="No API keys" description="Create a key to authenticate requests to api.hashset.ai." />}</section>{revoking && <ConfirmDialog prompt={REVOKE_API_KEY_PROMPT} busy={busy} onDismiss={() => setRevoking(null)} onConfirm={() => onRevoke(revoking)} />}</div>;
}

function UsagePage({events, buckets}: {events: DashboardData["usage"]; buckets: DashboardData["usageBuckets"]}) {
  return <div className="page-stack"><section className="panel page-panel"><div className="panel-heading"><div><h2>Hourly usage</h2><p>Requests, tokens, and spend grouped by model for the last seven days.</p></div></div><UsageBuckets buckets={buckets} /></section><section className="panel page-panel"><div className="panel-heading"><div><h2>Request history</h2><p>Settled usage delivered by the edge billing queue.</p></div></div><UsageRows events={events} /></section></div>;
}

export const BYOK_COST_NOTE =
  "Billed under the provider key configured for this account, not your Hashset balance.";

// BYOK traffic never touches the wallet, so its cost cell carries a label and an
// explanation rather than a dollar amount that would read as zero spend.
export function usageCost(billingMode: BillingMode, chargeMicros: number): {label: string; note: string | null} {
  return billingMode === "byok"
    ? {label: "BYOK", note: BYOK_COST_NOTE}
    : {label: formatDollars(chargeMicros), note: null};
}

function InfoTip({label, text}: {label: string; text: string}) {
  const describedBy = useId();
  const [anchor, setAnchor] = useState<{x: number; y: number} | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const place = useCallback(() => {
    const bounds = triggerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    // The bubble is centred on the trigger, so its anchor is kept half a bubble
    // away from either edge rather than letting the viewport squeeze it.
    const reach = Math.min(130, Math.max(0, (window.innerWidth - 24) / 2));
    const centre = bounds.left + bounds.width / 2;
    setAnchor({
      x: Math.min(Math.max(centre, 12 + reach), window.innerWidth - 12 - reach),
      y: bounds.top,
    });
  }, []);

  // The bubble is positioned against the viewport so the panel's clipped
  // overflow cannot cut it off, which means it has to track the trigger while
  // the page moves — including the scroll that focusing the trigger causes.
  const open = anchor !== null;
  useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  return <span className="info-tip">
    <button ref={triggerRef} type="button" aria-label={label} aria-describedby={describedBy}
      onPointerEnter={place} onPointerLeave={() => setAnchor(null)}
      onFocus={place} onBlur={() => setAnchor(null)}
      onClick={() => (anchor ? setAnchor(null) : place())}>
      <Icon name="info" />
    </button>
    <span id={describedBy} className="visually-hidden">{text}</span>
    {anchor && <span className="info-tip-bubble" role="presentation"
      style={{left: `${anchor.x}px`, top: `${anchor.y}px`}}>{text}</span>}
  </span>;
}

function CostCell({billingMode, chargeMicros}: {billingMode: BillingMode; chargeMicros: number}) {
  const cost = usageCost(billingMode, chargeMicros);
  if (!cost.note) return <span>{cost.label}</span>;
  return <span className="cost-cell">
    <span className="byok-badge">{cost.label}</span>
    <InfoTip label="How BYOK usage is billed" text={cost.note} />
  </span>;
}

function UsageBuckets({buckets}: {buckets: DashboardData["usageBuckets"]}) {
  if (!buckets.length) return <EmptyState title="No hourly usage yet" description="Hourly totals will appear after the first request settles." />;
  return <div className="analytics-table"><div className="analytics-head"><span>Hour</span><span>Model</span><span>Requests</span><span>Tokens</span><span>Spend</span></div>{buckets.map((bucket) => <div className="analytics-row" key={`${bucket.hour}:${bucket.model}:${bucket.billingMode}`}><span>{formatDate(bucket.hour)}</span><strong>{bucket.model}</strong><span>{formatNumber(bucket.requests)}</span><span>{formatNumber(bucket.inputTokens + bucket.outputTokens)}</span><CostCell billingMode={bucket.billingMode} chargeMicros={bucket.spendMicros} /></div>)}</div>;
}

function UsageRows({events, compact = false}: {events: DashboardData["usage"]; compact?: boolean}) {
  if (!events.length) return <EmptyState title="No usage yet" description="Requests will appear after their usage is settled." />;
  return <div className="usage-table"><div className="usage-head"><span>Model</span><span>Tokens</span><span>Cost</span>{!compact && <span>Completed</span>}</div>{events.map((event) => <div className="usage-row" key={event.requestId}><strong>{event.model}</strong><span>{formatNumber(event.inputTokens + event.outputTokens)}</span><CostCell billingMode={event.billingMode} chargeMicros={event.chargeMicros} />{!compact && <span>{formatDate(event.completedAt)}</span>}</div>)}</div>;
}

function BillingPage({wallet, busy, onCheckout}: {wallet: DashboardData["wallet"]; busy: boolean; onCheckout: (amount: number) => Promise<void>}) {
  const amounts = [10, 25, 50, 100];
  return <div className="page-stack"><section className="wallet-card"><p>Available balance</p><strong>{formatDollars(wallet.availableMicros)}</strong><div><span>{formatDollars(wallet.reservedMicros)} reserved</span><span>{formatDollars(wallet.pendingMicros)} settling</span></div></section><section className="panel topup-panel"><div className="panel-heading"><div><h2>Add credits</h2><p>Credits are charged once through Autumn and never expire.</p></div></div>{wallet.configured ? <div className="topup-grid">{amounts.map((amount) => <button key={amount} disabled={busy} onClick={() => void onCheckout(amount)}><strong>${amount}</strong><span>Add {amount.toLocaleString()} USD credits</span></button>)}</div> : <div className="notice error-notice"><strong>Billing setup required</strong><span>Configure the Autumn secret, credit feature, and one-off top-up plan.</span></div>}</section></div>;
}

function SecretDialog({apiKey, onClose}: {apiKey: CreatedApiKey; onClose: () => void}) {
  const [copied, setCopied] = useState(false);
  return <div className="dialog-backdrop" role="presentation"><section className="secret-dialog" role="dialog" aria-modal="true" aria-labelledby="key-created-title"><h2 id="key-created-title">Copy your secret now</h2><p>You won’t be able to see this key again.</p><div className="secret-value"><code>{apiKey.secret}</code><button aria-label="Copy API key" onClick={() => void navigator.clipboard.writeText(apiKey.secret).then(() => setCopied(true))}><Icon name="copy" /></button></div><span className="copy-state">{copied ? "Copied to clipboard" : "Store it in a password manager or secret store."}</span><button className="primary full" onClick={onClose}>I’ve saved the key</button></section></div>;
}

function LoadingState() { return <div className="loading-grid">{Array.from({length: 6}, (_, index) => <span key={index} />)}</div>; }
function EmptyState({title, description}: {title: string; description: string}) { return <div className="empty-state"><strong>{title}</strong><span>{description}</span></div>; }

export function App() {
  const {isLoading, user, signIn, signUp, signOut} = useSession();
  const pathname = usePathname();
  const dashboard = pathname.startsWith("/dashboard");
  const publicUsage = pathname === "/usage";
  const loginStarted = useRef(false);

  useEffect(() => {
    if (pathname !== "/login" || loginStarted.current) return;
    loginStarted.current = true;
    void signIn({state: {returnTo: "/dashboard"}}).catch(() => {
      loginStarted.current = false;
    });
  }, [pathname, signIn]);

  if (isLoading || pathname === "/login") {
    return <main className="setup-shell"><div className="brand-loader"><img src="/brand/icon-black.svg" alt="" /><span>Loading Hashset…</span></div></main>;
  }

  if (publicUsage) return <PublicUsagePage user={user} signIn={signIn} />;
  if (!dashboard) return <LandingPage user={user} signIn={signIn} signUp={signUp} />;
  if (!user) {
    return (
      <main className="setup-shell">
        <section className="auth-required">
          <img src="/brand/wordmark-black.svg" alt="Hashset" />
          <h1>Sign in to continue</h1>
          <p>Your dashboard session is not active. Sign in again to continue.</p>
          <button className="primary" onClick={() => void signIn({state: {returnTo: pathname}})}>Sign in</button>
          <a href="/">Back to home</a>
        </section>
      </main>
    );
  }
  const displayName = user?.firstName ?? user?.email?.split("@")[0] ?? "Account";
  return <Dashboard signOut={signOut} email={user?.email ?? ""} name={displayName} />;
}
